import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { bautizosAPI } from "@/lib/api";
import { Plus, Search, Eye, Edit, Trash2, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function BautizosPage() {
  const [bautizos, setBautizos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadBautizos();
  }, []);

  const loadBautizos = async (searchTerm = "") => {
    try {
      setLoading(true);
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await bautizosAPI.getAll(params);
      setBautizos(response.data);
    } catch (error) {
      toast.error("Error al cargar los bautizos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadBautizos(search);
  };

  const handleDelete = async () => {
    try {
      await bautizosAPI.delete(deleteId);
      toast.success("Bautizo eliminado correctamente");
      loadBautizos();
    } catch (error) {
      toast.error("Error al eliminar el bautizo");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="bautizos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Droplets className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
            Bautizos
          </h1>
          <p className="text-stone-600 mt-1">Gestión de registros de bautizos</p>
        </div>
        <Link to="/bautizos/nuevo">
          <Button className="bg-rose-950 hover:bg-rose-900 text-white" data-testid="nuevo-bautizo-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Bautizo
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Buscar por nombre, apellido o número de partida..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-white border-stone-200"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="h-11">
              Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-stone-800">
            {bautizos.length} registros encontrados
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-400 animate-pulse">
              Cargando...
            </div>
          ) : bautizos.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Droplets className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No se encontraron bautizos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-ledger">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-6 py-4">Partida</TableHead>
                    <TableHead className="px-6 py-4">Bautizado</TableHead>
                    <TableHead className="px-6 py-4">Fecha</TableHead>
                    <TableHead className="px-6 py-4">Ministro</TableHead>
                    <TableHead className="px-6 py-4 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bautizos.map((bautizo) => (
                    <TableRow key={bautizo.id}>
                      <TableCell className="px-6 py-4 font-medium">
                        {bautizo.numero_libro}-{bautizo.numero_folio}-{bautizo.numero_partida}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {bautizo.bautizado_nombre} {bautizo.bautizado_apellido}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {bautizo.fecha_bautizo}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {bautizo.ministro_nombre}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/bautizos/${bautizo.id}`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={`/bautizos/${bautizo.id}/editar`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(bautizo.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este bautizo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
